import React, {useEffect, useRef, useState} from 'react';
import detectElementOverflow from 'detect-element-overflow';

interface UseSelectLogicProps {
    data?: { [key: string]: any };
    value?: any;
    dropState?: boolean;
    parentRef?: React.RefObject<HTMLElement>;
    onChange: (value: any) => void;
    direction?: ('bottom' | 'left' | 'right' | 'top' | 'center')[];
    onDropShow?: () => void;
    onDropHide?: () => void;
    isMultiSelect?: boolean;
    disabled?: boolean;
}

function useSelectLogic(
    {

        data = {},
        value = null,
        dropState = false,
        parentRef,
        onChange,
        direction,
        onDropShow,
        onDropHide,
        isMultiSelect = false,
        disabled = false,
    }: UseSelectLogicProps) {

    const selectRef = useRef<HTMLDivElement | null>(null);
    const listRef = useRef<HTMLUListElement | null>(null);
    const [dropdown, setDropdown] = useState<boolean>(dropState || false);
    const [hoveredItem, setHoveredItem] = useState<number | null>(null);
    const [directionList, setDirectionList] = useState<
        ('bottom' | 'left' | 'right' | 'top' | 'center')[]
    >([]);

    useEffect(() => {
        handleCheckCollision();
    }, [dropdown, Object.entries(data).length]);

    const handleShow = async () => {
        if (disabled) return;
        if (onDropShow) await onDropShow();
        setDropdown(true);
        if (!Object.entries(data).length) return;
        setHoveredItem(Object.keys(data).indexOf(String(value)));
    };

    const handleHide = async (e?: React.FocusEvent<HTMLDivElement>) => {
        if (e && e.relatedTarget && selectRef.current?.contains(e.relatedTarget))
            return;

        if (onDropHide) await onDropHide();
        setDropdown(false);
        setHoveredItem(null);
        setDirectionList([]);
    };

    const handleSelect = async (key: number | string) => {
        if (isMultiSelect) {
            let newValue: any[];
            if (Array.isArray(value) && value.includes(key)) {
                newValue = value.filter((v: any) => v !== key);
            } else {
                newValue = [...(value || []), key];
            }
            onChange(newValue);
        } else {
            if (key != null) onChange(key);
            await handleHide();
            selectRef.current?.blur();
        }
    };

    const handleCheckCollision = () => {
        const collision: ('bottom' | 'left' | 'right' | 'top' | 'center')[] = [];

        if (!dropdown && !Object.entries(data).length) {
            return;
        }
        if (direction?.length) {
            setDirectionList(direction);
            return;
        }

        const listElement = listRef.current;
        const parentElement = parentRef?.current;

        if (listElement && parentElement) {
            const {
                collidedTop,
                collidedLeft,
                collidedBottom,
                collidedRight,
            } = detectElementOverflow(listElement, parentElement);

            if (collidedTop) collision.push('bottom');
            if (collidedRight) collision.push('left');
            if (collidedLeft) collision.push('right');
            if (collidedBottom) collision.push('top');
            if (!collidedLeft && !collidedBottom && !collidedRight && !collidedTop)
                collision.push('center');

            setDirectionList(collision);
        }
    };

    const handlePress = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.target !== selectRef.current) return;
        e.preventDefault();
        if (e.key === 'ArrowUp') handleUp();
        if (e.key === 'ArrowDown') handleDown();
        if (e.key === 'Enter')
            handleSelect(Object.keys(data)[hoveredItem ?? 0]);
    };

    const handleUp = () => {
        setHoveredItem((prev) =>
            prev && prev - 1 >= 0 ? prev - 1 : 0
        );
    };

    const handleDown = () => {
        setHoveredItem((prev) =>
            prev != null && prev + 1 < Object.keys(data).length
                ? prev + 1
                : Object.keys(data).length - 1
        );
    };

    return {
        selectRef,
        listRef,
        dropdown,
        setDropdown,
        hoveredItem,
        setHoveredItem,
        directionList,
        handleShow,
        handleHide,
        handleSelect,
        handlePress,
        handleUp,
        handleDown,
    };
}

export default useSelectLogic;
